/**
 * Entity refs (docs/analytics-assistant.md). A brief tags each entity with short refs, and the
 * model may link only as `[text](ref)`. The renderer turns a ref into a link only when it parses
 * here AND the brief it answered from listed it, so a model (or a game name carrying a prompt
 * injection) can never make us render a URL.
 */

export const REF_KINDS = ['game', 'config', 'perf', 'operator', 'operatorPerf', 'brand', 'alerts', 'page'] as const;
export type RefKind = (typeof REF_KINDS)[number];

/** The analytics pages a `page:<id>` ref may open, by their assistant page ids. */
export const ANALYTICS_PAGES = [
  'overview',
  'games',
  'operators',
  'alerts',
  'explore',
  'players',
  'retention',
  'funnel',
  'playerValue',
] as const;
export type AnalyticsPage = (typeof ANALYTICS_PAGES)[number];

const isAnalyticsPage = (value: string): value is AnalyticsPage =>
  (ANALYTICS_PAGES as readonly string[]).includes(value);

/** What a ref points at; the renderer maps each kind to its real route. */
export type RefTarget =
  | { kind: 'game' | 'config' | 'operator' | 'operatorPerf' | 'brand'; id: number }
  | { kind: 'perf'; titleKey: string }
  | { kind: 'alerts' }
  | { kind: 'page'; page: AnalyticsPage };

/** Everything `encodeURIComponent` leaves alone except the unreserved `._~-`: the ref stays one token. */
const encodeValue = (value: string) =>
  encodeURIComponent(value).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);

export const ref = {
  /** The game's catalog page. */
  game: (gameId: number) => `game:${gameId}`,
  /** The configs list filtered to the game. */
  config: (gameId: number) => `config:${gameId}`,
  /** The title's game analytics page. */
  perf: (titleKey: string) => `perf:${encodeValue(titleKey)}`,
  operator: (operatorId: number) => `operator:${operatorId}`,
  /** The operator's analytics page. */
  operatorPerf: (operatorId: number) => `operatorPerf:${operatorId}`,
  /** The backoffice brand id (the brand page's), not the analytics external brand id. */
  brand: (brandId: number) => `brand:${brandId}`,
  alerts: () => 'alerts',
  /** Another analytics page, keeping the filters the reader has on. */
  page: (page: AnalyticsPage) => `page:${page}`,
};

const REF = /^(game|config|perf|operator|operatorPerf|brand|page):([A-Za-z0-9%._~-]{1,300})$/;
const ID = /^[1-9]\d{0,9}$/;

/** Parses a ref; null for anything that isn't exactly one of the kinds above. */
export function parseRef(value: string): RefTarget | null {
  if (value === 'alerts') return { kind: 'alerts' };
  const match = REF.exec(value);
  if (!match) return null;
  const [, kind, raw = ''] = match;
  if (kind === 'page') return isAnalyticsPage(raw) ? { kind: 'page', page: raw } : null;
  if (kind === 'perf') {
    try {
      const titleKey = decodeURIComponent(raw);
      return titleKey.trim() ? { kind: 'perf', titleKey } : null;
    } catch {
      return null;
    }
  }
  if (!ID.test(raw)) return null;
  return { kind: kind as Exclude<RefKind, 'perf' | 'alerts' | 'page'>, id: Number(raw) };
}
